import { usePersistence } from '@segments/Storage'
import * as React from 'react'

import { TPersistedForm } from '~shared/segments/FormScreen'

type TOptions = {
  rememberForm?: Nullable<boolean>
}

/**
 * Loads persisted order form values if the customer wants them remembered.
 */
export const usePersistedForm = ({ rememberForm }: TOptions) => {
  //ensure that the persistence is loaded just once
  const refHasRun = React.useRef(false)
  // the persisted values loaded once at the first render (not updated)
  const [persistedForm, setPersistedForm] = React.useState<Nullable<TPersistedForm> | undefined>(
    undefined,
  )

  const [getPersistedForm] = usePersistence<TPersistedForm>({
    key: `orderForm`,
  })

  // try to load the persisted form values once (if the customer wants it)
  React.useEffect(() => {
    // persistence was already loaded
    if (refHasRun.current) {
      return
    }

    // the other persistence is not loaded yet
    if (rememberForm === undefined) {
      return
    }

    // at this point we can say that we tried to load the persistence (customer either wants it remembered or not)
    refHasRun.current = true

    // customer does not want the form to be remembered
    if (!rememberForm) {
      setPersistedForm(null)
      return
    }

    // customer wants the form to be restored -> load it from the persistence
    const loadStoredForm = async () => {
      const form = await getPersistedForm()
      setPersistedForm(form)
    }

    loadStoredForm()
  }, [getPersistedForm, rememberForm])

  return persistedForm
}
